import apiClient from './apiClient';
import { ApiResponse } from '../types';

const API_TYPE = 'api' as const;

export interface SetAutoLoginOnBehalfRequest {
  password?: string;
  totp_key?: string;
  auto_login_enabled?: boolean;
  api_key?: string;
  api_secret?: string;
}

export interface SetAutoLoginOnBehalfResponse {
  managed_user_id: string;
  managed_client_id: string;
  secret_was_created: boolean;
  auto_login_enabled: boolean | null;
}

export interface SetCapitalOnBehalfResponse {
  managed_user_id: string;
  managed_client_id: string;
  capital: number;
  previous_capital: number;
  /** True iff today's analytics-daily row was updated. False = today's row
   *  doesn't exist yet (16:10 IST cron hasn't run). null = capital unchanged. */
  analytics_row_updated: boolean | null;
}

export interface SetGroupOnBehalfResponse {
  managed_user_id: string;
  managed_client_id: string;
  /** New group_id, or null if cleared. */
  group_id: string | null;
  /** Previous group_id (null if the row had no tag). */
  previous_group_id: string | null;
}

class ManagedBrokerService {
  /** Configure auto-login for a managed user (admin-on-behalf, Phase 3.6).
   *  Backend lazy-creates the api-credentials secret if missing, mirrors the
   *  auto_login_enabled flag onto the managed user's broker-accounts row,
   *  and emits an audit log with acting_admin_user_id. */
  async setAutoLoginOnBehalf(
    managedUserId: string,
    managedClientId: string,
    request: SetAutoLoginOnBehalfRequest,
  ): Promise<SetAutoLoginOnBehalfResponse> {
    try {
      const response = await apiClient.put<ApiResponse<SetAutoLoginOnBehalfResponse>>(
        `/admin/managed-users/${managedUserId}/broker-accounts/${managedClientId}/auto-login`,
        request,
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to configure auto-login');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to configure auto-login',
      );
    }
  }

  /** Admin-on-behalf capital edit. Sets broker-accounts.capital for the managed
   *  user; backend best-effort propagates to today's analytics-daily row. */
  async setCapitalOnBehalf(
    managedUserId: string,
    managedClientId: string,
    capital: number,
  ): Promise<SetCapitalOnBehalfResponse> {
    try {
      const response = await apiClient.put<ApiResponse<SetCapitalOnBehalfResponse>>(
        `/admin/managed-users/${managedUserId}/broker-accounts/${managedClientId}/capital`,
        { capital },
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update capital');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to update capital',
      );
    }
  }

  /** Admin-on-behalf client-group tag — assign or clear (pass null to clear).
   *  Backend validates the group_id against client-groups (FK check). */
  async setGroupOnBehalf(
    managedUserId: string,
    managedClientId: string,
    groupId: string | null,
  ): Promise<SetGroupOnBehalfResponse> {
    try {
      const response = await apiClient.put<ApiResponse<SetGroupOnBehalfResponse>>(
        `/admin/managed-users/${managedUserId}/broker-accounts/${managedClientId}/group`,
        { group_id: groupId },
        undefined,
        API_TYPE,
      );
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to update group');
      }
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to update group',
      );
    }
  }
}

const managedBrokerService = new ManagedBrokerService();
export default managedBrokerService;
